import { useId } from 'react';
import { Mail } from 'lucide-react';
import { BUTTON_BASE } from './Button';

/**
 * Where a unit that wants to pilot the product writes to.
 *
 * One definition, because it is rendered on three surfaces — the front page, sign-in and About —
 * and an address that has drifted on one of them is a prospective unit writing into a void with
 * nothing on either side to say so.
 */
export const INFO_EMAIL = 'info@troopherald.com';

/**
 * What the product's availability actually is, and how to ask in (TROOP-217).
 *
 * **One component rather than three blocks of copy**, for the reason `DISCLAIMER` is one constant:
 * it says the same two things on the front page, on sign-in and in About, and the version that
 * writes them out each time is the one where a sentence about invitations survives on two screens
 * and not the third. About wraps it in more context; the front page makes it the destination of
 * the hero's own button (TROOP-220); the claim itself lives here.
 *
 * **Accurate rather than aspirational.** Beta and invite-only are both true today. There is no
 * waitlist and no queue being worked through — founding invites are issued by hand from
 * `AdminInvitesScreen`, an address and a note and a copyable link — so nothing here may imply a
 * signup that will be processed. Self-serve signup is TROOP-212 and is not built; when it is, this
 * copy is what has to change.
 *
 * **A `mailto:` and the address as text, never a form.** A request form is a public
 * unauthenticated write path: it would need rate limiting, storage and somewhere to read the
 * queue, and it would sit in front of a process that is already a human reading email — a table
 * added ahead of an inbox, changing nothing about what happens next. The address renders *beside*
 * the link rather than only inside it because a `mailto:` opens nothing on a device with no mail
 * client configured, while an address you can read and copy always works.
 *
 * **Not shown to somebody who already holds an invite.** `SignInScreen` renders this in `signin`
 * mode only: a founding invite and a youth claim link are both already the answer to *how do we
 * get in*, and offering the way to ask there reads as the invite not having worked.
 */
export function BetaNotice() {
  const headingId = useId();

  return (
    <section
      aria-labelledby={headingId}
      style={{
        background: 'var(--color-surface-raised)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-5)',
      }}
    >
      <h2
        id={headingId}
        className="t-subheading"
        style={{ color: 'var(--color-text-primary)', margin: 0, textWrap: 'balance' }}
      >
        In beta, by invitation
      </h2>

      <p
        className="t-body"
        style={{
          color: 'var(--color-text-secondary)',
          margin: 'var(--space-2) 0 0',
          maxWidth: '54ch',
        }}
      >
        Units join by invitation while the product is in beta. If yours would like to pilot it,
        write to us and say a little about the unit.
      </p>

      {/* An anchor rather than `Button`, which renders a `<button>`: this is a navigation, and a
          mailto belongs on the element browsers and assistive technology already understand. It
          wears the design system's own button geometry via `BUTTON_BASE` rather than a retyped
          copy of it — the first draft retyped the string and had already dropped the transition
          classes, so this was the one secondary control in the product with no hover animation and
          nothing to say so. */}
      <a
        href={`mailto:${INFO_EMAIL}`}
        className={`${BUTTON_BASE} th-btn--secondary`}
        style={{ marginTop: 'var(--space-4)' }}
      >
        <Mail size={16} aria-hidden="true" />
        Email us
      </a>

      <p
        className="t-caption"
        style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-3) 0 0' }}
      >
        Or copy the address:{' '}
        <span
          style={{
            userSelect: 'all',
            color: 'var(--color-text-primary)',
            overflowWrap: 'anywhere',
          }}
        >
          {INFO_EMAIL}
        </span>
      </p>
    </section>
  );
}
